import { ApolloCache, DefaultContext, MutationUpdaterFunction } from '@apollo/client';
import { GET_QUOTE_LIST } from 'connectors/queries/quote';
import { Quote } from 'types';
import { QUOTES_PER_REQUEST_LIMIT } from 'utils/constants';
import { get15DigitId } from 'utils/helpers/salesforce';

const getDefaultListQuotesResponseStructure = ({ queryVariables }) => {
  return {
    getAccount: {
      accountId: queryVariables.accountId,
      listQuotes: {
        count: 0,
        hasMore: false,
        limit: queryVariables.limit,
        quotes: [],
      },
    },
  };
};

type QuoteMutationType = MutationUpdaterFunction<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  DefaultContext,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ApolloCache<any>
>;
const removeQuoteFromCache = ({ cache, accountId, transactionId }) => {
  const queryVariables = {
    accountId,
    limit: QUOTES_PER_REQUEST_LIMIT,
    offset: 0,
    operation: 'remove',
  };

  const data =
    cache.readQuery({
      query: GET_QUOTE_LIST,
      variables: queryVariables,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) || getDefaultListQuotesResponseStructure({ queryVariables });

  if (!data?.getAccount?.listQuotes?.quotes?.length) {
    return;
  }

  // Write our data back to the cache.
  cache.writeQuery({
    query: GET_QUOTE_LIST,
    variables: queryVariables,
    data: {
      ...data,
      getAccount: {
        ...data.getAccount,
        listQuotes: {
          ...data.getAccount.listQuotes,
          count: data.getAccount.listQuotes.count + 1,
          // We keep the "null" quotes as they need to be counted for the limit/offset calculation
          quotes: data?.getAccount?.listQuotes?.quotes.filter((quote: Quote) =>
            quote ? quote.transactionId !== transactionId : true
          ),
        },
      },
    },
  });
};

const addItemOnCache = ({ cache, accountId, quoteData }) => {
  const queryVariables = {
    accountId,
    limit: QUOTES_PER_REQUEST_LIMIT,
    offset: 0,
    operation: 'add',
  };

  const data =
    cache.readQuery({
      query: GET_QUOTE_LIST,
      variables: queryVariables,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) || getDefaultListQuotesResponseStructure({ queryVariables });

  // Write our data back to the cache.
  cache.writeQuery({
    query: GET_QUOTE_LIST,
    variables: queryVariables,
    data: {
      ...data,
      getAccount: {
        ...data.getAccount,
        listQuotes: {
          ...data.getAccount.listQuotes,
          count: data.getAccount.listQuotes.count + 1,
          quotes: [quoteData],
        },
      },
    },
  });
};

export const alterQuoteMutationUpdate: QuoteMutationType = (cache, { data: { alterQuote } }) => {
  const { transactionId, accountId } = alterQuote;
  removeQuoteFromCache({
    cache,
    accountId: get15DigitId(accountId),
    transactionId,
  });
  addItemOnCache({ cache, accountId: get15DigitId(accountId), quoteData: alterQuote });
};

export const createQuoteMutationUpdate: QuoteMutationType = (cache, { data: { createQuote } }) => {
  const { accountId } = createQuote;
  addItemOnCache({ cache, accountId: get15DigitId(accountId), quoteData: createQuote });
};

export const cloneQuoteMutationUpdate: QuoteMutationType = (cache, { data: { cloneQuote } }) => {
  const { accountId } = cloneQuote;
  addItemOnCache({ cache, accountId: get15DigitId(accountId), quoteData: cloneQuote });
};

export const deleteQuoteMutationUpdate: QuoteMutationType = (cache, _, { variables }) => {
  const { accountId, transactionId } = variables;
  removeQuoteFromCache({
    cache,
    transactionId,
    accountId: get15DigitId(accountId),
  });
};
